import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/ApiConfig';
import DefaultModal from './DefaultModal';
import useDefaultModal from '../hooks/useDefaultModal';

function ImagemModal({ show, imagem, cnpj, onClose, onSave }) {
  const [orientacao, setOrientacao] = useState('R');
  const [file, setFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [validated, setValidated] = useState(false);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);
  const token = localStorage.getItem('token');

  const { setDefaultModal, showDefaultModal } = useDefaultModal();

  useEffect(() => {
    if (!show) return;

    if (imagem) {
      setOrientacao(imagem.orientacao || 'R');
      setFile(null);
      if (imagem.imagem) {
        setImagePreview(`data:image/png;base64,${imagem.imagem}`);
      } else {
        setImagePreview(null);
      }
    } else {
      setOrientacao('R');
      setFile(null);
      setImagePreview(null);
    }
    setValidated(false);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [show]);

  const processFile = file => {
    if (!file.type.startsWith('image/')) {
      showDefaultModal('error', 'Por favor, selecione um arquivo de imagem válido.');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      showDefaultModal('error', 'A imagem deve ter no máximo 5MB.');
      return;
    }

    setFile(file);

    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.onerror = () => {
      showDefaultModal('error', 'Erro ao ler o arquivo de imagem.');
    };
    reader.readAsDataURL(file);
  };

  const handleFileChange = e => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      processFile(selectedFile);
    }
    if (e.target) {
      e.target.value = '';
    }
  };

  const handleDrop = e => {
    e.preventDefault();
    e.stopPropagation();
    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) {
      processFile(droppedFile);
    }
  };

  const handleDragOver = e => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveImage = e => {
    e.stopPropagation();
    setFile(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = e => {
    e.preventDefault();
    const form = e.currentTarget;

    if (form.checkValidity() === false) {
      e.stopPropagation();
      setValidated(true);
      return;
    }

    let hasFile = !!file;
    if (imagem && imagem.imagem) {
      hasFile = true;
    }

    if (!hasFile) {
      showDefaultModal('error', 'Por favor, selecione uma imagem.');
      return;
    }

    setLoading(true);

    const formData = new FormData();
    formData.append('cnpj', cnpj);
    formData.append('orientacao', orientacao);

    if (file) {
      formData.append('file', file);
    } else if (imagem && imagem.imagem) {
      // Reutiliza o base64 atual da imagem, convertendo em Blob
      const byteCharacters = atob(imagem.imagem);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      formData.append('file', new Blob([byteArray], { type: 'image/png' }), 'imagem.png');
    }

    axios
      .post(`${API_BASE_URL.IMAGENS}/save`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
      })
      .then(() => {
        showDefaultModal(
          'success',
          imagem ? 'Imagem atualizada com sucesso!' : 'Imagem cadastrada com sucesso!'
        );
        onSave();
        onClose();
      })
      .catch(error => {
        console.error('Erro ao salvar imagem:', error);
        showDefaultModal('error', error.response?.data?.message || 'Erro ao salvar imagem!');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  if (!show) return null;

  return (
    <>
      <div
        className="modal show d-block"
        tabIndex="-1"
        style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      >
        <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
          <div className="modal-content">
<div
                className="modal-header text-white"
                style={{
                  background:
                    'linear-gradient(0deg, rgb(7, 99, 7) 0%, rgb(7, 59, 43) 100%)',
                  padding: '0.5rem 1rem',
                  borderTopLeftRadius: '0.5rem',
                  borderTopRightRadius: '0.5rem',
                  border: 'none',
                }}
              >
              <h6 className="modal-title mb-0">
                <i className="bi bi-image me-2"></i>
                {imagem ? 'Editar Imagem' : 'Nova Imagem'}
              </h6>
              <button
                type="button"
                className="btn-close btn-close-white"
                onClick={onClose}
                disabled={loading}
              ></button>
            </div>
            <div className="modal-body">
              <form
                noValidate
                validated={validated.toString()}
                onSubmit={handleSubmit}
                id="imagemForm"
              >
                <div className="row g-3">
                  <div className="col-md-12">
                    <label htmlFor="orientacao" className="form-label">
                      Orientação <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select"
                      id="orientacao"
                      name="orientacao"
                      value={orientacao}
                      onChange={e => setOrientacao(e.target.value)}
                      required
                      disabled={!!imagem}
                    >
                      <option value="R">Retrato (Vertical)</option>
                      <option value="P">Paisagem (Horizontal)</option>
                    </select>
                    <div className="invalid-feedback">Campo obrigatório.</div>
                  </div>

                  <div className="col-12">
                    <label className="form-label">
                      Imagem <span className="text-danger">*</span>
                      {file && (
                        <span className="badge bg-success ms-2">
                          <i className="bi bi-check-circle me-1"></i>
                          Carregada ({Math.round(file.size / 1024)}KB)
                        </span>
                      )}
                    </label>

                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      style={{ display: 'none' }}
                    />

                    <div
                      className="border rounded p-4 text-center"
                      style={{
                        borderStyle: 'dashed',
                        borderColor: '#dee2e6',
                        backgroundColor: '#f8f9fa',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                      }}
                      onDrop={handleDrop}
                      onDragOver={handleDragOver}
                      onClick={handleClick}
                      role="button"
                      tabIndex={0}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          handleClick();
                        }
                      }}
                    >
                      {imagePreview ? (
                        <div className="position-relative d-inline-block">
                          <img
                            src={imagePreview}
                            alt="Preview da imagem"
                            style={{
                              maxWidth: '100%',
                              maxHeight: '300px',
                              objectFit: 'contain',
                              borderRadius: '4px',
                            }}
                          />
                          <button
                            type="button"
                            className="btn btn-danger btn-sm position-absolute top-0 end-0 m-2"
                            onClick={handleRemoveImage}
                            title="Remover imagem"
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                          <div className="mt-3">
                            <small className="text-muted d-block">
                              Clique para selecionar outra imagem
                            </small>
                          </div>
                        </div>
                      ) : (
                        <div style={{ padding: '20px' }}>
                          <i
                            className="bi bi-cloud-upload"
                            style={{ fontSize: '3rem', color: '#6c757d', display: 'block', marginBottom: '10px' }}
                          ></i>
                          <p className="mb-2" style={{ fontSize: '1rem', fontWeight: '500' }}>
                            Selecione uma imagem
                          </p>
                          <p className="mb-3" style={{ color: '#6c757d' }}>
                            Arraste e solte um arquivo aqui ou clique para procurar
                          </p>
                          <small className="text-muted d-block">
                            Formatos aceitos: PNG, JPG, JPEG (máx. 5MB)
                          </small>
                          <button
                            type="button"
                            className="btn btn-primary btn-sm mt-3"
                            onClick={e => {
                              e.stopPropagation();
                              handleClick();
                            }}
                          >
                            <i className="bi bi-folder2-open me-1"></i>
                            Procurar Arquivo
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </form>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={onClose}
                disabled={loading}
              >
                Cancelar
              </button>
              <button
                type="submit"
                form="imagemForm"
                className="btn btn-primary btn-sm"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Salvando...
                  </>
                ) : (
                  <>
                    <i className="bi bi-check-circle me-2"></i>
                    Salvar
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      <DefaultModal {...setDefaultModal} />
    </>
  );
}

export default ImagemModal;